import { useEffect } from "react";
import { Shield, Target, Zap, Users, Award } from "lucide-react";
import { motion } from "framer-motion";

export default function AboutUs() {
  // Fixes the "not showing from top" issue
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* 
          HERO SECTION 
          - Professional, bold typography
          - Neobrutalist border separation
      */}
      <section className="py-20 px-6 border-b-4 border-foreground bg-primary/5">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">
              Pioneering Social <br />
              <span className="text-primary italic">Automation Excellence.</span>
            </h1>
            <p className="max-w-3xl text-xl font-bold text-muted-foreground leading-relaxed">
              DmFlio provides enterprise-grade infrastructure for creators and businesses to bridge the gap between social engagement and meaningful conversion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 
          MISSION & VISION 
          - Structured, high-contrast blocks
      */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="p-10 border-4 border-foreground shadow-brutal bg-white">
            <Target className="w-12 h-12 mb-6 text-primary" strokeWidth={2.5} />
            <h2 className="text-3xl font-black uppercase mb-4 tracking-tight">Our Mission</h2>
            <p className="font-bold text-muted-foreground leading-relaxed">
              To eliminate the friction of manual social media management. We empower creators to scale their influence by automating repetitive tasks through secure, official API protocols, allowing them to focus on high-value content creation.
            </p>
          </div>

          <div className="p-10 border-4 border-foreground shadow-brutal bg-accent/10">
            <Shield className="w-12 h-12 mb-6 text-foreground" strokeWidth={2.5} />
            <h2 className="text-3xl font-black uppercase mb-4 tracking-tight">Compliance & Safety</h2>
            <p className="font-bold text-muted-foreground leading-relaxed">
              Security is our core priority. DmFlio is built exclusively on the Official Meta Messaging API. We ensure 100% compliance with platform policies, providing a secure environment that protects user accounts while maximizing reach.
            </p>
          </div>
        </div>
      </section>

      {/* 
          CORE PILLARS 
          - Professional icon-based list
      */}
      <section className="py-20 px-6 bg-foreground text-white border-y-4 border-foreground">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black uppercase mb-12 text-center tracking-tighter italic">Why the Industry Chooses DmFlio</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-4">
              <Zap className="w-10 h-10 mx-auto text-primary" />
              <h3 className="text-xl font-black uppercase">Instant Execution</h3>
              <p className="font-medium text-gray-400">Average response time under 1.8 seconds via optimized webhooks.</p>
            </div>
            <div className="space-y-4">
              <Users className="w-10 h-10 mx-auto text-primary" />
              <h3 className="text-xl font-black uppercase">Creator First</h3>
              <p className="font-medium text-gray-400">Architected for scale, serving millions of interactions monthly.</p>
            </div>
            <div className="space-y-4">
              <Award className="w-10 h-10 mx-auto text-primary" />
              <h3 className="text-xl font-black uppercase">Official Access</h3>
              <p className="font-medium text-gray-400">Direct integration with Meta Graph API for maximum stability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 
          ABOUT DMFLIO SECTION
          - Company overview and information
      */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="space-y-8">
          <div className="border-b-4 border-foreground pb-8">
            <h2 className="text-4xl font-black uppercase mb-6 tracking-tighter italic">About DmFlio</h2>
            <div className="space-y-4">
              <p className="text-lg font-bold text-muted-foreground leading-relaxed">
                DmFlio is a SaaS platform that helps businesses automate customer communication across Instagram and Facebook using official Meta integrations.
              </p>
              <p className="text-lg font-bold text-muted-foreground leading-relaxed">
                Our platform enables businesses, creators, and brands to manage conversations, automate responses, capture leads, and improve customer engagement through a centralized dashboard.
              </p>
              <p className="text-lg font-bold text-muted-foreground leading-relaxed">
                DmFlio is operated by EXEMPLORA CONSUMER PRIVATE LIMITED, based in Hyderabad, India.
              </p>
              <p className="text-lg font-bold text-muted-foreground leading-relaxed">
                We focus on providing reliable, secure, and compliant automation tools that help businesses streamline communication while following Meta platform policies and best practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 
          FOUNDER SECTION
          - Professional CSE branding with founder image
      */}
      <section className="py-20 px-6 max-w-7xl mx-auto border-t-4 border-foreground">
        <div className="bg-white border-4 border-foreground p-6 md:p-8 flex flex-col md:flex-row items-center gap-8 md:gap-12 shadow-brutal transition-transform hover:-rotate-1">
          <div className="w-44 h-44 md:w-56 md:h-56 border-4 border-foreground shadow-brutal-sm flex-shrink-0 overflow-hidden bg-muted">
            <img
              src="/Aazam_Co-Founder_DmFlio.jpg"
              alt="Aazam Shareeff, Founder & Lead Engineer"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-4 text-center md:text-left w-full md:w-auto">
            <div className="inline-block px-3 py-1 bg-primary text-white font-black uppercase text-[10px] tracking-widest border-2 border-foreground">Founder & Lead Engineer</div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter leading-tight">Aazam Shareeff, <span className="text-primary italic">CSE</span></h2>
            <p className="text-base md:text-lg font-bold text-muted-foreground leading-relaxed italic">
              "DmFlio was built to solve a single problem: human speed. We help creators deliver value at the speed of their community's curiosity, ensuring no lead is ever left in a comment section."
            </p>
            <p className="text-sm md:text-base font-bold text-muted-foreground leading-relaxed">
              An engineer at heart, Aazam founded DmFlio to solve the scalability challenges faced by modern digital creators. With a background in Computer Science Engineering, he leads the technical vision—ensuring that DmFlio remains the most stable and compliant automation engine in the market.
            </p>
          </div>
        </div>
      </section>

      {/* 
          CONTACT & FOOTER CALL TO ACTION 
      */}
      <section className="py-20 px-6 text-center border-t-4 border-foreground">
        <h2 className="text-2xl font-black uppercase mb-4 tracking-tight">Technical & Support Inquiries</h2>
        <p className="font-bold text-xl mb-8">hello@dmflio.com</p>
        <div className="flex justify-center gap-4">
          <div className="px-6 py-2 border-2 border-foreground font-black uppercase text-sm">Meta Business Partner</div>
          <div className="px-6 py-2 border-2 border-foreground font-black uppercase text-sm">GDPR Compliant</div>
        </div>
      </section>
    </div>
  );
}